import { lazy, Suspense, useMemo } from 'react'
import { FlaskConical } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { useOrganization } from '../contexts/OrganizationContext'
import { analyticsApi, organizationApi, postApi } from '../services/api'
import { hasProductAction, PRODUCT_KEYS } from '../config/studioProducts'
import { ROLES, ROLE_HIERARCHY } from '../constants/roles'
import PostPreviewModal from '../components/PostPreviewModal'
import ExperimentsPage from './ExperimentsPage'
import { Button, PageHeader, PageLayout, StatePanel, Tabs } from '../components/common'
import styles from './GrowthLabPage.module.css'

// The dashboard and the legacy report tables load on demand: an operator
// opening Experiments should not pay for chart and table code.
const GrowthDashboard = lazy(() => import('../components/growth/GrowthDashboard').then((module) => ({ default: module.GrowthDashboard })))
const AnalyticsPage = lazy(() => import('./AnalyticsPage'))

// Performance and Experiments are still being flushed out with the product
// team, so operator roles only get the dashboard (#538). Every tab needs Feed
// "analyze", which every dashboard widget reads through and the Studio API
// grants to editors and up by default.
const TABS = Object.freeze([
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'performance', label: 'Performance', minRole: ROLES.INTERNAL_ORG_ADMIN },
  { key: 'experiments', label: 'Experiments', minRole: ROLES.INTERNAL_ORG_ADMIN },
])

// Older links keep working: the dashboard replaced the attribution mockup,
// and the reports tab is the Performance page it grew out of.
const TAB_ALIASES = {
  dashboard: 'dashboard',
  overview: 'dashboard',
  attribution: 'dashboard',
  recommendations: 'dashboard',
  performance: 'performance',
  reports: 'performance',
  tests: 'experiments',
  experiments: 'experiments',
}

// The dashboard is still being shaped with the product team, so the tab says
// so rather than looking like settled ground.
const dashboardTabLabel = (
  <span className={styles.experimentalTab}>
    Dashboard
    <FlaskConical size={14} aria-hidden="true" />
    <span className={styles.srOnly}>, experimental</span>
  </span>
)

function formatDateTime(value) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

// A tab the role cannot see, asked for directly or through an alias, falls
// back to the dashboard.
function getActiveTab(searchParams, availableTabs) {
  const requested = TAB_ALIASES[searchParams.get('tab') || 'dashboard'] || 'dashboard'
  return availableTabs.some((tab) => tab.key === requested) ? requested : 'dashboard'
}

function GrowthLabPage() {
  const { currentOrg } = useOrganization()
  const slug = currentOrg?.slug
  const [searchParams, setSearchParams] = useSearchParams()
  const roleRank = ROLE_HIERARCHY[currentOrg?.role] || 0
  const canAnalyze = hasProductAction(currentOrg, PRODUCT_KEYS.FEED, 'analyze')
  const availableTabs = useMemo(
    () => (canAnalyze ? TABS.filter((tab) => !tab.minRole || roleRank >= ROLE_HIERARCHY[tab.minRole]) : []),
    [canAnalyze, roleRank],
  )
  const activeTab = getActiveTab(searchParams, availableTabs)
  const createSignal = searchParams.get('newTest')
  const selectedPostId = searchParams.get('post')

  const timelineQuery = useQuery({
    queryKey: ['growth', 'timeline', slug],
    queryFn: () => analyticsApi.getTimeline(slug),
    enabled: Boolean(slug) && activeTab === 'performance',
  })

  const postQuery = useQuery({
    queryKey: ['growth', 'post-preview', slug, selectedPostId],
    queryFn: () => postApi.get(slug, selectedPostId),
    enabled: Boolean(slug && selectedPostId),
  })

  const brandingQuery = useQuery({
    queryKey: ['growth', 'post-preview-branding', currentOrg?.id],
    queryFn: () => organizationApi.getBranding(currentOrg.id),
    enabled: Boolean(currentOrg?.id && selectedPostId),
  })

  const timelineItems = timelineQuery.data?.items || []
  const chartAnnotations = timelineItems
    .filter((item) => item.title)
    .slice(0, 3)
    .map((item) => ({
      key: `${item.kind}-${item.id}`,
      label: `${item.title}${item.happened_at ? ` · ${formatDateTime(item.happened_at)}` : ''}`,
    }))
  const previewPost = postQuery.data && String(postQuery.data.id) === selectedPostId ? postQuery.data : null

  const updateTab = (nextTab) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (nextTab === 'dashboard') next.delete('tab')
      else next.set('tab', nextTab)
      if (nextTab !== 'experiments') {
        next.delete('newTest')
        next.delete('experiment')
      }
      if (nextTab !== 'performance') next.delete('view')
      return next
    })
  }

  const updateReportView = (nextView) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      if (nextView === 'overview') next.delete('view')
      else next.set('view', nextView)
      return next
    })
  }

  const openPostPreview = (postId) => {
    if (postId == null || postId === '') return
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('post', String(postId))
      return next
    })
  }

  const closePostPreview = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.delete('post')
      return next
    })
  }

  const openNewTest = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('tab', 'experiments')
      next.delete('experiment')
      next.set('newTest', String(Date.now()))
      return next
    })
  }

  const selectExperiment = (experimentId) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set('tab', 'experiments')
      if (experimentId) next.set('experiment', experimentId)
      else next.delete('experiment')
      return next
    })
  }

  const pageActions = activeTab === 'experiments' ? <Button onClick={openNewTest}>New test</Button> : null

  const dashboardContent = (
    <Suspense fallback={<StatePanel state="loading" title="Loading dashboard" compact />}>
      <GrowthDashboard product={PRODUCT_KEYS.FEED} org={currentOrg} onOpenPost={openPostPreview} />
    </Suspense>
  )

  const performanceContent = (
    <Suspense fallback={<StatePanel state="loading" title="Loading performance" compact />}>
      <div className={styles.surfacePanel}>
        <AnalyticsPage
          embedded
          view={searchParams.get('view') || 'overview'}
          onViewChange={updateReportView}
          onPostClick={(post) => openPostPreview(post?.post_id)}
          annotations={chartAnnotations}
        />
      </div>
    </Suspense>
  )

  const experimentsContent = (
    <div className={styles.surfacePanel}>
      <ExperimentsPage
        embedded
        createSignal={createSignal}
        selectedExperimentId={searchParams.get('experiment')}
        onSelectedExperimentChange={selectExperiment}
      />
    </div>
  )

  const panels = { dashboard: dashboardContent, performance: performanceContent, experiments: experimentsContent }
  const activeTabLabel = availableTabs.find((tab) => tab.key === activeTab)?.label
  let content
  if (!slug) {
    content = <StatePanel state="restricted" title="Select an organization" description="Choose an organization before opening Growth." />
  } else if (availableTabs.length === 0) {
    content = (
      <StatePanel
        state="restricted"
        title="Analytics access required"
        description="This account can open Wager Feed, but does not have analytics access for this product."
      />
    )
  } else {
    content = (
      <Tabs
        className={styles.growthTabs}
        label="Growth sections"
        value={activeTab}
        onValueChange={updateTab}
        items={availableTabs.map((tab) => ({ value: tab.key, label: tab.key === 'dashboard' ? dashboardTabLabel : tab.label, panel: panels[tab.key] }))}
      />
    )
  }

  return (
    <PageLayout size="wide">
      <PageHeader title="Growth" context={activeTabLabel ? `Feed / ${activeTabLabel}` : 'Feed'} actions={pageActions} />
      <div className={styles.content}>{content}</div>
      {selectedPostId && (postQuery.isLoading || (postQuery.isFetching && !previewPost)) ? (
        <StatePanel state="loading" title="Loading post preview" compact />
      ) : null}
      {selectedPostId && postQuery.error && !postQuery.isFetching ? (
        <StatePanel
          state="error"
          title="Post preview could not load"
          description={postQuery.error.message || String(postQuery.error)}
          onRetry={postQuery.refetch}
          compact
        />
      ) : null}
      <PostPreviewModal
        open={Boolean(previewPost)}
        post={previewPost}
        branding={brandingQuery.data}
        onClose={closePostPreview}
      />
    </PageLayout>
  )
}

export default GrowthLabPage
